import React, { Dispatch } from "react";
import Typography from "@mui/material/Typography";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  styled,
} from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
import EditIcon from "@mui/icons-material/Edit";
import { blue, green, grey } from "@mui/material/colors";
import { featuresToMultiLine, featureTo2Lines } from "fe-common/logic";
import CloseIcon from "@mui/icons-material/Close";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  CONDENSED_FONT,
  CondensedTypography,
} from "fe-web-components/styles/typography";
import { GraphDetailSelection } from "../util/graphDetialsSelectionHook";
import { SupportFocusMutation } from "./responsiveGridLayout";
import {
  cutPopulationFromFeatures,
  inexactLift,
  inexactProb,
  rule,
} from "../util/graphDetialUtils";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { isProduction } from "fe-common/utils/envGetter";
import { MobileView } from "../util/aKaunGeneralUtil";
import { useScreenSize } from "./diagnosticAgent/util/useScreenSize";
import { RuleStatFragment, RuleType } from "fe-common/graphql/types";
import {
  GraphDataEdge,
  GraphDataNode,
  QualifiedFeature,
} from "fe-common/graphql";
import { ResourceCountDisplay } from "fe-web-components/citations/ResourceCountDisplay";
import { ArticleLink } from "fe-web-components/citations/ArticlesLink";
import { saveCitationInLocalStorage } from "fe-common/backoffice/SaveCitationToLocal";

//region [[ Styles ]]

const RuleValueTitle = styled("div")(({ theme }) => ({
  color: KahunColorMap.Primary,
  fontFamily: CONDENSED_FONT,
  fontSize: "18px",
  // JM: I cant seem to take the font from a theme parameter - the next line doesnt work
  // fontFamily: theme.typography.caption.fontFamily,
  fontWeight: 500,

  marginBottom: "3px",
  width: "100%",

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
    color: KahunColorMap.BlueReferences,
  },
}));

const SourceText = styled("span")({
  fontWeight: 600,
});

const ArticleLinkStyled = styled(ArticleLink)({
  textDecoration: "none !important",
});

const ResourceContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    margin: "-7",
    marginBottom: "4vh",
  },
}));

const TargetText = styled("span")({
  fontWeight: 600,
});

const QualifierDetails = styled("div")({
  fontFamily: CONDENSED_FONT,
  fontSize: "14px",
  fontWeight: 500,
});

const ActionsItem = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
});

const CitationDetails = styled("div")({
  fontFamily: CONDENSED_FONT,
  display: "block",
  fontSize: "14px",
  fontWeight: 500,
});

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    backgroundColor: "white",
  },

  "&:hover": {
    backgroundColor: "white",
  },
  flexDirection: "column",
  alignItems: "start",
  padding: "0",
  marginBottom: "10px",
}));

const CitationContainer = styled("div")(({ theme }) => ({
  color: KahunColorMap.DefaultLight,
  fontSize: "14px",
}));

const ResourcesHeader = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {},
}));

const EdgeContainer = styled("div")(({ theme }) => ({
  marginTop: "10px",
  [theme.breakpoints.down(MobileView)]: {
    marginTop: "0px",
  },
}));

const EdgeTitle = styled("div")(({ theme }) => ({
  fontFamily: CONDENSED_FONT,
  display: "none",
  fontSize: "16px",
  [theme.breakpoints.down(MobileView)]: {
    display: "none",
  },
  color: "black",
  marginTop: "2vh",
  fontWeight: 500,
}));

//endregion [[ Styles ]]

//region [[ Props ]]
export interface GraphAreaSelection {
  node?: GraphDataNode;
  edge?: {
    path: GraphDataEdge;
    source: GraphDataNode;
    target: GraphDataNode;
  }[];
  detailOpen?: boolean;
}

//endregion [[ Props ]]

//region [[ Function ]]

export const prob = (e: Partial<RuleStatFragment>) => {
  switch (e.type) {
    case RuleType.Isa:
      return "";
    case RuleType.Lift:
      return e.ruleValue.exactCitation
        ? `with lift ${e.lift} x`
        : inexactLift(e.lift);
    default:
      return e.ruleValue.exactCitation
        ? `in ${formatN(e.prob)}% of cases`
        : inexactProb(e.prob);
  }
};

function formatN(n: number) {
  return (n * 100.0).toFixed(1).padStart(3, " ");
}

//endregion [[ Functions ]]

export function getArticles({
  selection,
  details,
  disease,
  directEdge,
  edgesAroundSelection,
  nodes,
}: GraphDetailSelection): {
  count: number;
  paths: {
    path: GraphDataEdge;
    source: GraphDataNode;
    target: GraphDataNode;
  }[];
} {
  let paths = details.edge;
  if (selection.node) {
    paths = edgesAroundSelection?.map((path) => {
      const [source, target] = nodes(path);
      return { path, source, target };
    });
    // move direct path to top
    let dedge = directEdge(selection.node);
    if (dedge) {
      paths.sort((a, b) => (a.path.id === dedge.id ? -1 : 0));
    }
  }

  let count =
    paths &&
    paths.reduce(
      (acc, e) =>
        acc +
        (e.path.ruleValues
          ? e.path.ruleValues.filter((rv) => !!rv.ruleValue.citationsObj).length
          : 0),
      0,
    );

  return { count, paths };
}

function getSubTypesShown(
  paths: {
    path: GraphDataEdge;
    source: GraphDataNode;
    target: GraphDataNode;
  }[],
) {
  if (!paths) return [];
  const rules = paths && paths.flatMap((p) => p.path.ruleValues || []);
  const subtypes = new Map<string, QualifiedFeature>();
  rules.forEach((r) => {
    if (r.overloadSourceType === RuleType.Subtype)
      r.overloadSource?.forEach((qf) => subtypes.set(qf.id, qf));
    if (r.overloadTargetType === RuleType.Subtype)
      r.overloadTarget?.forEach((qf) => subtypes.set(qf.id, qf));
  });
  return [...subtypes.values()];
}

export function GraphResourceHeader({
  selection,
  fullTitle,
  className,
}: {
  fullTitle?: boolean;
  selection: GraphDetailSelection;
  className?: string;
}) {
  const { count: articlesCount, paths } = getArticles(selection);
  const subtypes = getSubTypesShown(paths);

  return (
    <ResourcesHeader>
      <ResourceCountDisplay
        articlesCount={articlesCount}
        className={className}
        fullTitle={fullTitle ? "REFERENCES" : ""}
      />
      {subtypes && subtypes.length > 0 && false && (
        <CondensedTypography display="inline">
          {"Via "}
          {subtypes.map((st) => featureTo2Lines(st)[0]).join(", ")}
        </CondensedTypography>
      )}
    </ResourcesHeader>
  );
}

export function GraphDetails(
  props: GraphDetailSelection & {
    showClose?: boolean;
    onClose: Dispatch<null>;
    showInternalLinks?: boolean;
  } & SupportFocusMutation,
) {
  let { selection, showClose, onClose, showInternalLinks } = props;
  let classes = useStyles({});
  let open = selection && (selection.edge || selection.node);

  const { isMobile } = useScreenSize();

  const articles = getArticles(props);
  return (
    <ResourceContainer>
      <Box
        zIndex={3}
        hidden={!open}
        flex={1}
        display="flex"
        flexDirection="column"
        padding={"10px"}
        className={classes.citationsPanel}
        width={"100%"}
      >
        {showClose && (
          <Box display="flex" justifyContent={"flex-end"}>
            <Button onClick={onClose}>
              <CloseIcon />
            </Button>
          </Box>
        )}
        <GraphResourceHeader selection={props} fullTitle={true} />
        <Box flexDirection={"column"} flex={1} overflow="auto">
          {articles &&
            articles.paths?.map((e: any, index) => {
              let sources = cutPopulationFromFeatures(
                e.path.overloadSource || e.source.qfs,
              );

              return (
                <span key={index}>
                  {e.path.ruleValues.length > 1 && (
                    <EdgeTitle>
                      {featuresToMultiLine(sources.features, ",")}
                      {" - "}
                      {featuresToMultiLine(
                        e.path.overloadTarget || e.target.qfs,
                        ",",
                      )}
                    </EdgeTitle>
                  )}
                  <EdgeText
                    isMobile={isMobile}
                    key={index}
                    path={e.path}
                    source={e.source}
                    target={e.target}
                    showInternalLinks={showInternalLinks}
                  />
                  {index < articles.paths.length - 1 && <Divider />}
                </span>
              );
            })}
        </Box>
      </Box>
    </ResourceContainer>
  );
}

// TODO: components should move to thier own files 🏡
function RuleValueDetail({
  source,
  target,
  rv,
  className,
}: {
  source: GraphDataNode;
  target: GraphDataNode;
  rv: Pick<RuleStatFragment, "type" | "lift" | "prob"> &
    Partial<
      Pick<
        RuleStatFragment,
        | "overloadSource"
        | "overloadTarget"
        | "overloadSourceType"
        | "overloadTargetType"
      >
    >;
  className?: string;
}) {
  // if (rv.type === RuleType.TargetCauseSource) {
  //   let b = source;
  //   source = target;
  //   target = b;
  // }
  let sources = cutPopulationFromFeatures(rv.overloadSource || source.qfs);

  const getRelationProbability = () => {
    if (rule(rv?.type) === "causes") {
      if (prob(rv) === "Common") {
        return "commonly causes";
      } else if (prob(rv) === "Probable") {
        return "may cause";
      } else if (prob(rv) === "Very Common") {
        return "very commonly causes";
      } else if (prob(rv) === "Infrequent") {
        return "infrequently causes";
      } else if (prob(rv) === "Rare") {
        return "rarely causes";
      } else {
        return "causes";
      }
    } else {
      return rule(rv?.type);
    }
  };

  const sourceColor =
    rv.overloadSourceType != null
      ? KahunColorMap.Accent
      : KahunColorMap.BlueReferences;
  const targetColor =
    rv.overloadTargetType != null
      ? KahunColorMap.Accent
      : KahunColorMap.BlueReferences;
  return (
    <>
      <RuleValueTitle>
        <SourceText style={{ color: sourceColor }}>
          {featuresToMultiLine(sources.features, ",")}
        </SourceText>{" "}
        {getRelationProbability()}{" "}
        <TargetText style={{ color: targetColor }}>
          {featuresToMultiLine(rv.overloadTarget || target.qfs, ",")}
        </TargetText>{" "}
        {prob(rv).startsWith("in") ? prob(rv) : ""}
      </RuleValueTitle>

      <QualifierDetails>
        {sources.populationQualifiers
          .map((p) => `${p.title}:  ${p.value?.name}`)
          .join(" | ")}
      </QualifierDetails>
    </>
  );
}

function EdgeText({
  path,
  source,
  target,
  editRule,
  showInternalLinks,
  isMobile,
}: {
  path: GraphDataEdge;
  source: GraphDataNode;
  target: GraphDataNode;
  editRule?: (id: string) => void;
  showInternalLinks?: boolean;
  isMobile?: boolean;
}) {
  let classes = useStyles({});
  // if this is etiology rule, we have previously flipped source and target to display correctly
  // on graph. So need to unflip it now so texts come out right...
  if (path.type === RuleType.TargetCauseSource) {
    let b = source;
    source = target;
    target = b;
  }

  // if we have exact citations, skip the inexact ones
  // let ruleValues =  path?.ruleValues?.filter(rv=>rv.exactCitation);
  // if (!ruleValues || ruleValues.length==0) {
  //   ruleValues = path?.ruleValues?.slice(0,1);
  // }
  let ruleValues = path?.ruleValues;

  return (
    <EdgeContainer>
      <List>
        {path.type === RuleType.Isa ? (
          <ListItemStyled dense disableGutters={true}>
            <ListItemText
              primary={
                <RuleValueDetail
                  source={source}
                  target={target}
                  rv={path}
                  className={classes.citation}
                />
              }
            />
          </ListItemStyled>
        ) : (
          ruleValues &&
          ruleValues
            .filter(({ ruleValue: rv }) => rv.citationsObj)
            .map((rs, index: number) => {
              const link = rs.ruleValue.citationsObj?.url;
              const citation = rs.ruleValue.citationsObj?.vancouver;
              const citationId = rs.ruleValue.citationsObj?.id;

              return (
                <React.Fragment key={rs.ruleValue.ruleValueId + "_" + index}>
                  <ListItemStyled dense disableGutters={true}>
                    <ListItemText
                      primary={
                        <RuleValueDetail
                          className={classes.citation}
                          source={source}
                          target={target}
                          rv={rs}
                        />
                      }
                      secondary={
                        <CitationContainer>{citation}</CitationContainer>
                      }
                    />

                    {!isProduction() && showInternalLinks && (
                      <OpenInNewIcon
                        fontSize={"small"}
                        style={{
                          marginRight: "7px",
                          marginTop: "2px",
                          opacity: 0.05,
                        }}
                        onClick={() => {
                          saveCitationInLocalStorage(rs.ruleValue.citationsObj);

                          window.open(
                            "https://editor.kahun.com/#/w-kahun/citationEdit/" +
                              citationId,
                            "_blank",
                          );

                          //hashHistory.push(linkTo("citationEdit/" + rule.citation.id));
                          //dispatch(editCitationInCanvas(rule.citation.id));
                        }}
                      />
                    )}

                    {rs.ruleValue.ruleValueId && editRule && (
                      <ListItemSecondaryAction>
                        {rs.ruleValue.ruleValueId && editRule && (
                          <IconButton
                            onClick={() => editRule(rs.ruleValue.ruleValueId)}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    )}

                    <ActionsItem>
                      <CitationDetails>
                        <span>
                          <ArticleLinkStyled link={link}>
                            <span>Read More</span>
                          </ArticleLinkStyled>
                        </span>
                      </CitationDetails>
                    </ActionsItem>
                  </ListItemStyled>
                </React.Fragment>
              );
            })
        )}
      </List>
    </EdgeContainer>
  );
}

export function CitationTypography({ citation }: { citation: string }) {
  return (
    <Typography variant="body2" color="textSecondary" paragraph>
      <span
        dangerouslySetInnerHTML={{
          __html: citation.replace('href="', 'href="https://'),
        }}
      />
    </Typography>
  );
}

// TODO: translte styles to styled components
//#region styles
const useStyles = makeStyles((theme?: any) =>
  createStyles({
    avatarMain: {
      color: "#fff",
      backgroundColor: green[500],
    },
    avatarSecondary: {
      color: "#fff",
      backgroundColor: grey[200],
    },
    avatarISA: {
      color: "#fff",
      backgroundColor: blue[400],
    },
    citation: {
      color: KahunColorMap.Primary,
    },
    citationIcon: {
      color: KahunColorMap.Primary,
      "min-width": "30px",
      "min-height": "90px",
    },
    citationsPanel: {
      //borderTop: `6px solid ${KahunColorMap.DefaultLight}`,
      "padding-top": "10px",
      "padding-left": "10px",
      height: "100%",
      overflow: "auto",
    },
  }),
);
//#endregion
